import React, { type ReactNode, type FC } from 'react';
import Heading from '@atlaskit/heading';
import SectionMessage from '@atlaskit/section-message';
import { fg } from '@atlassian/jira-feature-gating';
import { getIdPHost } from '@atlassian/jira-global-permissions/src/common/utils';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	BANNER,
} from '@atlassian/jira-product-analytics-bridge';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import messages from './messages';

export const EnableAiBanner: FC = () => {
	const { formatMessage } = useIntl();
	const { data: orgId } = useOrgId();
	const isSiteAdmin = useIsSiteAdmin();
	const host = getIdPHost();

	const getLinkButton = (chunks: ReactNode[], href: string) => (
		<a href={href} rel="noopener noreferrer" target="_blank">
			{chunks}
		</a>
	);

	const messageIfSiteAdmin = fg('virtual_agent_rebrand_to_virtual_service_agent')
		? messages.bodyIfSiteAdminV2
		: messages.bodyIfSiteAdmin;

	const messageIfNotSiteAdmin = fg('virtual_agent_rebrand_to_virtual_service_agent')
		? messages.bodyIfNotSiteAdminV2
		: messages.bodyIfNotSiteAdmin;

	return (
		<ContextualAnalyticsData
			sourceType={BANNER}
			sourceName="servicedeskVirtualAgentLandingActionSectionEnableAi"
		>
			<SectionMessage appearance="information">
				<Heading size="small">{formatMessage(messages.heading)}</Heading>
				<p>
					{isSiteAdmin ? formatMessage(messageIfSiteAdmin) : formatMessage(messageIfNotSiteAdmin)}
				</p>
				<p>
					{isSiteAdmin
						? formatMessage(messages.linkIfSiteAdmin, {
								link1: (...chunks: ReactNode[]) =>
									getLinkButton(chunks, `${host}/o/${orgId}/atlassian-intelligence`),
								link2: (...chunks: ReactNode[]) =>
									getLinkButton(
										chunks,
										'https://support.atlassian.com/organization-administration/docs/activate-atlassian-intelligence-for-products/',
									),
							})
						: formatMessage(messages.linkIfNotSiteAdmin, {
								link: (...chunks: ReactNode[]) =>
									getLinkButton(
										chunks,
										'https://support.atlassian.com/organization-administration/docs/activate-atlassian-intelligence-for-products/',
									),
							})}
				</p>
			</SectionMessage>
			{/* https://data-portal.internal.atlassian.com/analytics/registry/61468 */}
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};
